import { useState, useEffect, useCallback } from 'react';
import { roofAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Home } from 'lucide-react';

const RoofMeasurement = () => {
  const [address, setAddress] = useState('');
  const [measurement, setMeasurement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const data = await roofAPI.getAll();
      setHistory(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching history:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleMeasure = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMeasurement(null);

    try {
      const result = await roofAPI.measure(address);
      setMeasurement(result);
      setAddress('');
      fetchHistory();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error measuring roof:', error);
      }
      alert('Failed to measure roof. Please check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">Roof Measurement Tool</h1>
        <p className="font-manrope text-gray-600">Get instant roof estimates from satellite data</p>
      </div>

      <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-8">
        <CardHeader>
          <CardTitle className="font-barlow text-2xl uppercase">Measure New Roof</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMeasure} className="space-y-4">
            <div>
              <label className="block font-manrope font-semibold mb-2">
                Enter Property Address
              </label>
              <Input
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Orlando, FL 32801"
                className="border-2 border-black"
              />
              <p className="text-sm text-gray-500 mt-2">
                Include street, city, state, and ZIP for best results
              </p>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000]"
            >
              {loading ? 'Measuring...' : 'Measure Roof'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {measurement && (
        <Card className="border-2 border-primary shadow-[6px_6px_0px_0px_#FF8C00] mb-8 bg-accent-light">
          <CardHeader>
            <CardTitle className="font-barlow text-2xl uppercase flex items-center gap-2">
              <Home className="h-6 w-6 text-primary" />
              Measurement Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-primary" />
                  <p className="font-manrope font-semibold">{measurement.address}</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-white rounded-sm border border-black">
                    <span className="font-manrope text-gray-600">Square Footage:</span>
                    <span className="font-barlow text-2xl font-bold text-primary">
                      {measurement.square_footage?.toLocaleString()} sq ft
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-sm border border-black">
                    <span className="font-manrope text-gray-600">Estimated Cost:</span>
                    <span className="font-barlow text-2xl font-bold text-green-600">
                      ${measurement.estimated_cost?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-sm border-2 border-black">
                  <p className="font-manrope font-semibold mb-2">Roof Details:</p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Type:</strong> {measurement.roof_type}</p>
                    <p><strong>Pitch:</strong> {measurement.pitch}</p>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">{measurement.notes}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
        <CardHeader>
          <CardTitle className="font-barlow text-2xl uppercase">Measurement History</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="font-manrope text-gray-600">No measurements yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-sm">
                  <div className="flex-1">
                    <p className="font-manrope font-semibold mb-1">{item.address}</p>
                    <p className="text-sm text-gray-600">
                      {item.square_footage?.toLocaleString()} sq ft • ${item.estimated_cost?.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RoofMeasurement;
